import React, { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import clsx from 'clsx';
import { Tab, TabGroup, TabList, TabPanel, TabPanels } from '@headlessui/react';
import {
  EllipsisHorizontalIcon,
  EnvelopeIcon,
  GlobeAltIcon,
  MapPinIcon,
  PhoneIcon,
  ArrowTopRightOnSquareIcon,
} from '@heroicons/react/16/solid';
import { SidebarLayout } from './common/Catalyst/sidebar-layout';
import AppSidebar from './AppSidebar';
import { Navbar } from './common/Catalyst/navbar';
import { Divider } from './common/Catalyst/divider';
import { HoaReturn, HoaUpdate, HoaMessageRequest } from './clients/rust-api-client';
import { hoaService, notificationService } from './clients/ApiClients';
import {
  ErrorMessage,
  Field,
  FieldGroup,
  Fieldset,
  Label,
} from './common/Catalyst/fieldset';
import { Textarea } from './common/Catalyst/textarea';
import { Input } from './common/Catalyst/input';
import { Button } from './common/Catalyst/button';
import { Dialog, DialogBody, DialogTitle } from './common/Catalyst/dialog';
import ViolationList from './ViolationList';
import { CreateActions } from './shell/CreateActions';
import VendorList from './VendorList';
import LoadingSpinner from './LoadingSpinner';
import XMarkIcon from '@heroicons/react/24/outline/XMarkIcon';
import {
  Dropdown,
  DropdownButton,
  DropdownItem,
  DropdownMenu,
} from './common/Catalyst/dropdown';
import usStates from './utils/addressUtils';
import { FieldPicker } from './common/Custom/FieldPicker';
import { formatPhoneNumber, stripPhoneFormatting } from './utils/phoneUtils';
import ShowAlert from './ShowAlert';
import HomeListComponent from './home/HomeListComponent';
import MemberListComponent from './member/MemberListComponent';
import SendHoaMessageDialog from './hoa/SendHOAMessageDialog';
import HoaBankAccountList from './HoaBankAccountList';
import HoaTransferList from './HoaTransferList';
import HoaCheckRegister from './HoaCheckRegister';
import HoaStopPayments from './HoaStopPayments';
import HoaTransferScheduleList from './HoaTransferScheduleList';
import HoaPayerFileList from './HoaPayerFileList';
import PayerAutoUploadPanel from './PayerAutoUploadPanel';
import { getUserTypeFromToken } from './utils/TokenUtils';
import BoardImpersonationPanel from './hoa/BoardImpersonationPanel';
import TimezonePanel from './hoa/TimezonePanel';
import { timezoneLabel } from './hoa/timezones';
import {
  canSetBoardImpersonation as canSetBoardImpersonationFor,
  canSetHoaTimezone as canSetHoaTimezoneFor,
} from './constants/UserTypes';
import { useSenderName } from './hooks/UserContext';
import {
  hasManagerAccess,
  canSubmitPayerFile,
  canViewLedger,
  canManageReservationPolicy,
  canViewReservations,
} from './constants/UserTypes';
import { ReservationPolicyPanel } from './amenity/ReservationPolicyPanel';
import { fresher, useSyncedHoa } from './sync/hooks';
import { apiErrorMessage } from './utils/apiErrors';
import { checkboxClass } from './reservation/formStyles';

interface TouchedFields {
  [key: string]: boolean;
}

interface AlertState {
  type: 'success' | 'error';
  title: string;
  message: string;
  show: boolean;
}

interface FormErrors {
  name?: string;
  street?: string;
  city?: string;
  state?: string;
  zip?: string;
  country?: string;
  contact_phone?: string;
  contact_email?: string;
  website_url?: string;
}

const HOADetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  // Billing is its own set of routes rather than another section on this
  // page — see docs/DUES_FRONTEND_PLAN.md. BoardMembers can read it.
  const canSeeBilling = canViewLedger(getUserTypeFromToken());
  // Bank accounts section is HOA-scoped and visible to Manager+ (mirrors the
  // backend HOA-access check); assign/unassign inside it is Owner/Admin only.
  const canViewHoaBankAccounts = hasManagerAccess(getUserTypeFromToken());
  // Payer files are Owner/Admin/SuperAdmin on the backend (both the submit
  // and the history reads), so the section is gated separately from the
  // Manager-visible accounts/transfers above it.
  const canViewPayerFiles = canSubmitPayerFile(getUserTypeFromToken());
  // Owner/Admin only, mirroring `can_set_board_impersonation`. The board
  // is the party this setting governs, so it cannot be the party that
  // decides — and a Manager is excluded too, because this is a standing
  // policy decision about who may read homeowners' financial history
  // rather than day-to-day association work.
  const canSetBoardImpersonation =
    canSetBoardImpersonationFor(getUserTypeFromToken());
  // Owner/Admin/Manager, mirroring `can_set_timezone` (#599); the server
  // also limits a Manager to their assigned HOAs. Everyone sees which
  // zone is in use.
  const canSetHoaTimezone = canSetHoaTimezoneFor(getUserTypeFromToken());
  // Amenity reservations (#642): every staff role may look; the policy —
  // whether to suspend booking for an unpaid balance, above all — is
  // Owner/Admin, beside the board-impersonation setting for the same
  // reason that one is: a standing decision, not day-to-day work.
  const canSeeReservations = canViewReservations(getUserTypeFromToken());
  const canSetReservationPolicy = canManageReservationPolicy(getUserTypeFromToken());
  const [fetchedHoa, setHoa] = useState<HoaReturn | null>(null);
  // From the sync engine's local copy when it has it (#561): instant, and
  // an edit from anywhere shows up here. Fetched otherwise.
  const syncedHoa = useSyncedHoa(id);
  const hoa = fresher(syncedHoa, fetchedHoa);
  const [loading, setLoading] = useState(true);
  // Bumped whenever a stop payment is placed or released, so the register's
  // Stopped badge and the stop list stay in step without sharing state.
  const [stopsVersion, setStopsVersion] = useState(0);
  const [showSaveError, setShowSaveError] = useState(false);
  const [saveErrorTitle, setSaveErrorTitle] = useState<string>('');
  const [saveError, setSaveError] = useState<string>('');
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [showFetchError, setShowFetchError] = useState(false);
  const [fetchErrorTitle, setFetchErrorTitle] = useState<string>('');
  const [fetchError, setFetchError] = useState<string>('');
  const [isEditing, setIsEditing] = useState(false);
  const [editedHoa, setEditedHoa] = useState<HoaReturn | null>(null);
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [touchedFields, setTouchedFields] = useState<TouchedFields>({});
  const [isMessageDialogOpen, setIsMessageDialogOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [, setAlert] = useState<AlertState>({
    type: 'success',
    title: '',
    message: '',
    show: false,
  });

  const senderName = useSenderName();

  const haveSynced = !!syncedHoa;
  useEffect(() => {
    const fetchHoa = async () => {
      if (haveSynced) {
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        const response = await hoaService.getHoa({
          id: id!,
        });
        setHoa(response.data);
      } catch (err) {
        console.error('Error fetching HOA:', err);
        const errorMessage = apiErrorMessage(err, "Couldn't load the HOA.");

        setFetchErrorTitle("Couldn't load HOA details");
        setFetchError(errorMessage);
        setShowFetchError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchHoa();
  }, [id, haveSynced]);

  const showAlert = (
    type: 'success' | 'error',
    title: string,
    message: string
  ) => {
    setAlert({
      type,
      title,
      message,
      show: true,
    });
  };

  // A failure is left to propagate: the dialog stays open and says why.
  const handleSendMessage = async (messageData: HoaMessageRequest) => {
    await notificationService.sendHoaMessage({
      hoaMessageRequest: messageData,
    });
    showAlert('success', 'Message Sent', 'Your message has been sent to all HOA members.');
  };

  const handleEdit = () => {
    setEditedHoa(hoa);
    setIsEditing(true);
    setTouchedFields({});
    setFormErrors({});
  };

  const handleSave = async () => {
    if (!editedHoa) return;

    if (!isFormValid()) {
      const allTouched: TouchedFields = {};
      Object.keys(editedHoa).forEach((key) => {
        allTouched[key] = true;
      });
      setTouchedFields(allTouched);
      return;
    }

    setIsSaving(true);
    setShowSaveError(false);
    setShowSuccessAlert(false);

    try {
      // Extract only the fields that can be updated
      const { id, company_id, date_created, date_updated, ...updateFields } = editedHoa;
      const hoaUpdate: HoaUpdate = updateFields;

      const response = await hoaService.updateHoa({
        id: editedHoa.id,
        hoaUpdate,
      });
      setHoa(response.data);
      setIsEditing(false);
      setShowSuccessAlert(true);
    } catch (err) {
      console.error('Error updating HOA:', err);
      const errorMessage = apiErrorMessage(err, "Couldn't save the HOA.");

      setSaveErrorTitle("Couldn't update HOA");
      setSaveError(errorMessage);
      setShowSaveError(true);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditedHoa(hoa);
    setFormErrors({});
    setTouchedFields({});
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    if (!editedHoa) return;
    const { name, value } = e.target;

    if (name === 'contact_phone') {
      const cleanedPhone = stripPhoneFormatting(value);
      setEditedHoa((prev) => {
        if (!prev) return null;
        return {
          ...prev,
          [name]: cleanedPhone, // Store raw digits in state
        } as HoaReturn;
      });
      setTouchedFields((prev) => ({ ...prev, [name]: true }));

      const error = validateField(name as keyof HoaReturn, cleanedPhone);
      setFormErrors((prev) => {
        const newErrors = { ...prev };
        if (error) {
          newErrors[name as keyof FormErrors] = error;
        } else {
          delete newErrors[name as keyof FormErrors];
        }
        return newErrors;
      });
      return;
    }

    setEditedHoa((prev) => {
      if (!prev) return null;
      return {
        ...prev,
        [name]: value,
      } as HoaReturn;
    });
    setTouchedFields((prev) => ({ ...prev, [name]: true }));

    const error = validateField(name as keyof HoaReturn, value);
    setFormErrors((prev) => {
      const newErrors = { ...prev };
      if (error) {
        newErrors[name as keyof FormErrors] = error;
      } else {
        delete newErrors[name as keyof FormErrors];
      }
      return newErrors;
    });
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!editedHoa) return;
    const { name, checked } = e.target;
    setEditedHoa((prev) => ({ ...prev, [name]: checked }) as HoaReturn);
  };

  const handleBlur = (
    e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setTouchedFields((prev) => ({ ...prev, [name]: true }));

    const error = validateField(name as keyof HoaReturn, value);
    setFormErrors((prev) => {
      const newErrors = { ...prev };
      if (error) {
        newErrors[name as keyof FormErrors] = error;
      } else {
        delete newErrors[name as keyof FormErrors];
      }
      return newErrors;
    });
  };

  const validateField = (
    name: keyof HoaReturn,
    value: any
  ): string | undefined => {
    switch (name) {
      case 'name':
        return !value ? 'Name is required' : undefined;
      case 'street':
        return !value ? 'Street address is required' : undefined;
      case 'city':
        return !value ? 'City is required' : undefined;
      case 'state':
        return !value ? 'State is required' : undefined;
      case 'zip':
        return !value
          ? 'ZIP code is required'
          : !/^\d{5}(-\d{4})?$/.test(value.toString())
            ? 'Invalid ZIP code format'
            : undefined;
      case 'contact_phone':
        const cleanedPhone = stripPhoneFormatting(value?.toString() || '');
        return !cleanedPhone
          ? 'Contact phone is required'
          : !/^\d{10,11}$/.test(cleanedPhone)
            ? 'Phone number must be 10 or 11 digits'
            : undefined;
      case 'contact_email':
        return !value
          ? 'Contact email is required'
          : !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.toString())
            ? 'Invalid email format'
            : undefined;
      case 'website_url':
        if (!value) return undefined;
        try {
          new URL(value.toString());
          return undefined;
        } catch {
          return 'Invalid website URL format';
        }
      default:
        return undefined;
    }
  };

  const isFormValid = () => {
    // Check for any existing errors
    if (Object.keys(formErrors).length > 0) {
      return false;
    }

    // Required fields from HoaReturn
    const requiredFields = [
      'name',
      'street',
      'city',
      'state',
      'zip',
      'contact_phone',
      'contact_email',
    ];

    if (!editedHoa) return false;

    return requiredFields.every((field) => {
      const value = editedHoa[field as keyof HoaReturn];
      return value !== undefined && value !== '';
    });
  };

  const handleDialogClose = () => {
    setEditedHoa(hoa);
    setIsEditing(false);
    setFormErrors({});
    setTouchedFields({});
  };

  // With the row in the sync store there is nothing to wait for: render
  // it on the first paint, not after the effect clears `loading` (#635).
  if (loading && !haveSynced) {
    return <LoadingSpinner></LoadingSpinner>;
  }

  // Tabs (#603 follow-on 2). The tab lives in the URL — ?tab=homes — so a
  // tab can be linked to and Back returns to it. Banking is only offered
  // where the existing gate allows it; Billing is its own page and its tab
  // is a link.
  const tabs = [
    { key: 'overview', label: 'Overview' },
    { key: 'homes', label: 'Homes' },
    { key: 'violations', label: 'Violations' },
    { key: 'members', label: 'Members' },
    { key: 'vendors', label: 'Vendors' },
    ...(canViewHoaBankAccounts ? [{ key: 'banking', label: 'Banking' }] : []),
  ];
  const requestedTab = searchParams.get('tab');
  const tabIndex = Math.max(0, tabs.findIndex((t) => t.key === requestedTab));
  const selectTab = (index: number) => {
    const next = new URLSearchParams(searchParams);
    if (index === 0) next.delete('tab');
    else next.set('tab', tabs[index].key);
    setSearchParams(next, { replace: true });
  };

  const address = hoa
    ? [hoa.street, hoa.city, [hoa.state, hoa.zip].filter(Boolean).join(' ')]
        .filter(Boolean)
        .join(', ')
    : '';
  const chip =
    'inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-[13px] text-fg-secondary';
  // No colour transition: Headless UI registers tabs in an effect, so its
  // first render selects tab 0 before settling on the one in the URL, and
  // a transition turned that into a visible slide from Overview on every
  // deep link.
  const tabClass =
    'relative -mb-px border-b-2 border-transparent px-1 pb-2.5 text-[13px] font-medium text-fg-muted ' +
    'hover:text-fg focus:outline-none data-[focus]:text-fg data-[selected]:border-accent data-[selected]:text-fg';

  return (
    <SidebarLayout
      sidebar={<AppSidebar currentPage="hoa_detail" />}
      navbar={<Navbar>{/* Your navbar content */}</Navbar>}
    >
      {/* Header strip: where this sits, and the overflow menu. */}
      <div className="-mx-2 mb-6 flex items-center gap-2 border-b border-line/60 px-2 pb-3 text-[13px]">
        <Link to="/hoas" className="text-fg-muted hover:text-fg">
          HOAs
        </Link>
        <span className="text-fg-faint" aria-hidden>
          /
        </span>
        <span className="min-w-0 truncate font-medium text-fg">{hoa?.name}</span>
        <div className="ml-auto flex items-center gap-2">
          {/* Creates for this association: its homes, its vendors. */}
          {hoa && <CreateActions scope={{ hoaId: hoa.id }} violation workOrder />}
          <Dropdown>
            <DropdownButton plain aria-label="More actions" disabled={!hoa}>
              <EllipsisHorizontalIcon className="h-4 w-4" />
            </DropdownButton>
            <DropdownMenu anchor="bottom end">
              <DropdownItem onClick={handleEdit}>Edit details…</DropdownItem>
              <DropdownItem onClick={() => setIsMessageDialogOpen(true)}>
                Message all members…
              </DropdownItem>
            </DropdownMenu>
          </Dropdown>
        </div>
      </div>

      <ShowAlert
        alertType="success"
        alertTitle="Success"
        alertMessage="HOA updated successfully!"
        isVisible={showSuccessAlert}
        onClose={() => setShowSuccessAlert(false)}
        timeout={5000}
        isInline={true}
      />
      <ShowAlert
        alertType="error"
        alertTitle={fetchErrorTitle}
        alertMessage={fetchError}
        isVisible={showFetchError}
        onClose={() => setShowFetchError(false)}
        timeout={5000}
        isInline={true}
      />

      {/* Title and properties — Linear's project page (#603 Part 2 §4). */}
      <h1 className="text-2xl/8 font-semibold tracking-tight text-fg">{hoa?.name}</h1>
      {hoa?.description && (
        <p className="mt-1 text-[15px]/6 text-fg-muted">{hoa.description}</p>
      )}
      {hoa && (
        <dl className="mt-5 flex flex-wrap items-center gap-x-1 gap-y-1">
          <dt className="mr-2 text-[13px] text-fg-muted">Properties</dt>
          <dd className={chip}>
            <span
              className={clsx('h-2 w-2 rounded-full', hoa.is_active ? 'bg-success' : 'bg-fg-faint')}
              aria-hidden
            />
            {hoa.is_active ? 'Active' : 'Inactive'}
          </dd>
          {address && (
            <dd className={chip}>
              <MapPinIcon className="h-3.5 w-3.5 text-fg-muted" aria-hidden />
              {address}
            </dd>
          )}
          {hoa.contact_email && (
            <dd>
              <a href={`mailto:${hoa.contact_email}`} className={clsx(chip, 'hover:bg-fg/5')}>
                <EnvelopeIcon className="h-3.5 w-3.5 text-fg-muted" aria-hidden />
                {hoa.contact_email}
              </a>
            </dd>
          )}
          {hoa.contact_phone && (
            <dd className={chip}>
              <PhoneIcon className="h-3.5 w-3.5 text-fg-muted" aria-hidden />
              {formatPhoneNumber(hoa.contact_phone)}
            </dd>
          )}
          {hoa.website_url && (
            <dd>
              <a
                href={hoa.website_url}
                target="_blank"
                rel="noopener noreferrer"
                className={clsx(chip, 'hover:bg-fg/5')}
              >
                <GlobeAltIcon className="h-3.5 w-3.5 text-fg-muted" aria-hidden />
                {hoa.website_url.replace(/^https?:\/\//, '')}
              </a>
            </dd>
          )}
        </dl>
      )}

      {hoa && (
        <TabGroup selectedIndex={tabIndex} onChange={selectTab} className="mt-8">
          <div className="flex items-end gap-5 border-b border-line">
            <TabList className="flex gap-5" aria-label="Association sections">
              {tabs.map((t) => (
                <Tab key={t.key} className={tabClass}>
                  {t.label}
                </Tab>
              ))}
            </TabList>
            {canSeeBilling && (
              <Link
                to={`/hoa/${id}/billing`}
                className="-mb-px inline-flex items-center gap-1 border-b-2 border-transparent px-1 pb-2.5 text-[13px] font-medium text-fg-muted hover:text-fg"
              >
                Billing
                <ArrowTopRightOnSquareIcon className="h-3 w-3" aria-hidden />
              </Link>
            )}
            {/* From main (#598): the association's own books. Same gate as
                Billing, and deliberately a sibling rather than a tab inside
                it: the books are a different axis from a homeowner's
                account. A link out, like Billing, since it is its own set
                of routes. */}
            {canSeeBilling && (
              <Link
                to={`/hoa/${id}/accounting`}
                className="-mb-px inline-flex items-center gap-1 border-b-2 border-transparent px-1 pb-2.5 text-[13px] font-medium text-fg-muted hover:text-fg"
              >
                Accounting
                <ArrowTopRightOnSquareIcon className="h-3 w-3" aria-hidden />
              </Link>
            )}
            {canSeeReservations && (
              <>
                <Link
                  to={`/hoa/${id}/reservations`}
                  className="-mb-px inline-flex items-center gap-1 border-b-2 border-transparent px-1 pb-2.5 text-[13px] font-medium text-fg-muted hover:text-fg"
                >
                  Reservations
                  <ArrowTopRightOnSquareIcon className="h-3 w-3" aria-hidden />
                </Link>
                <Link
                  to={`/hoa/${id}/amenities`}
                  className="-mb-px inline-flex items-center gap-1 border-b-2 border-transparent px-1 pb-2.5 text-[13px] font-medium text-fg-muted hover:text-fg"
                >
                  Amenities
                  <ArrowTopRightOnSquareIcon className="h-3 w-3" aria-hidden />
                </Link>
                <Link
                  to={`/hoa/${id}/reservations/utilization`}
                  className="-mb-px inline-flex items-center gap-1 border-b-2 border-transparent px-1 pb-2.5 text-[13px] font-medium text-fg-muted hover:text-fg"
                >
                  Utilization
                  <ArrowTopRightOnSquareIcon className="h-3 w-3" aria-hidden />
                </Link>
                <Link
                  to={`/hoa/${id}/reservations/revenue`}
                  className="-mb-px inline-flex items-center gap-1 border-b-2 border-transparent px-1 pb-2.5 text-[13px] font-medium text-fg-muted hover:text-fg"
                >
                  Amenity revenue
                  <ArrowTopRightOnSquareIcon className="h-3 w-3" aria-hidden />
                </Link>
              </>
            )}
          </div>
          <TabPanels className="pt-6">
            <TabPanel className="focus:outline-none">
              <dl className="grid max-w-2xl grid-cols-[8rem_1fr] gap-x-6 gap-y-3 text-[13px]">
                <dt className="text-fg-muted">About</dt>
                <dd className="text-fg">{hoa.description || '—'}</dd>
                <dt className="text-fg-muted">Address</dt>
                <dd className="text-fg">{address || '—'}</dd>
                <dt className="text-fg-muted">Email</dt>
                <dd className="text-fg">{hoa.contact_email || '—'}</dd>
                <dt className="text-fg-muted">Phone</dt>
                <dd className="text-fg">
                  {hoa.contact_phone ? formatPhoneNumber(hoa.contact_phone) : '—'}
                </dd>
                <dt className="text-fg-muted">Website</dt>
                <dd className="break-all text-fg">{hoa.website_url || '—'}</dd>
                <dt className="text-fg-muted">Timezone</dt>
                <dd className="text-fg">
                  {timezoneLabel(hoa.timezone)}
                  <span className="text-fg-muted">
                    {hoa.timezone_override ? ' · set explicitly' : ' · from the state'}
                  </span>
                </dd>
              </dl>
              {/* From main (#593): who may read homeowners' portals on
                  the board's behalf. On Overview, with the association's
                  own details -- NOT inside the Banking tab: this is a
                  standing decision about who may read homeowners'
                  financial history, it has nothing to do with bank
                  accounts, and nesting it there would mean narrowing bank
                  access silently hid a security setting. Its own gate is
                  `canSetBoardImpersonation`. */}
              {canSetBoardImpersonation && (
                <div className="mt-8 max-w-2xl">
                  <BoardImpersonationPanel
                    hoaId={hoa.id}
                    enabled={hoa.board_member_impersonation}
                    onChanged={(enabled) =>
                      setHoa({ ...hoa, board_member_impersonation: enabled })
                    }
                  />
                </div>
              )}
              {canSetHoaTimezone && (
                <div className="mt-8 max-w-2xl">
                  <TimezonePanel hoa={hoa} onChanged={setHoa} />
                </div>
              )}
              {canSetReservationPolicy && (
                <div className="mt-8 max-w-2xl">
                  <ReservationPolicyPanel hoaId={hoa.id} />
                </div>
              )}
            </TabPanel>
            <TabPanel className="focus:outline-none">
              <HomeListComponent hoaId={hoa.id} />
            </TabPanel>
            <TabPanel className="focus:outline-none">
              <ViolationList hoaId={hoa.id} title="Violations" />
            </TabPanel>
            <TabPanel className="focus:outline-none">
              <MemberListComponent hoaId={hoa.id} fetchAll={true} />
            </TabPanel>
            <TabPanel className="focus:outline-none">
              <VendorList hoaId={hoa.id} />
            </TabPanel>
            {canViewHoaBankAccounts && (
              <TabPanel className="space-y-8 focus:outline-none">
                <HoaBankAccountList hoaId={hoa.id} />
                {/* Schedules sit ABOVE the transfer history on purpose. The
                    panel leads with a needs-attention block for scheduled
                    transfers whose outcome is unknown, and that is the one
                    thing here that requires a person — it must not sit
                    below a transfer table that grows without bound. */}
                <HoaTransferScheduleList hoaId={hoa.id} />
                <HoaTransferList hoaId={hoa.id} />
                {/* The issued-check register. Every check recorded here posts
                    to the GL and is registered with the bank's Positive Pay
                    service, so a presented check the bank cannot match gets
                    flagged in Online Banking. */}
                <HoaCheckRegister
                  hoaId={hoa.id}
                  refreshKey={stopsVersion}
                  onStopPlaced={() => setStopsVersion((v) => v + 1)}
                />
                {/* Stop payments: the bank told to refuse a check. Placed
                    from a register row above or by number here; a stop
                    does not void the check. */}
                <HoaStopPayments
                  hoaId={hoa.id}
                  refreshKey={stopsVersion}
                  onChanged={() => setStopsVersion((v) => v + 1)}
                />
                {canViewPayerFiles && (
                  <>
                    <HoaPayerFileList hoaId={hoa.id} />
                    <PayerAutoUploadPanel
                      hoaId={hoa.id}
                      enabled={hoa.payer_file_auto_upload}
                      onChanged={(enabled) =>
                        setHoa({ ...hoa, payer_file_auto_upload: enabled })
                      }
                    />
                  </>
                )}
              </TabPanel>
            )}
          </TabPanels>
        </TabGroup>
      )}

      <Dialog
        open={isEditing}
        onClose={handleDialogClose}
        className="w-full max-w-2xl mx-auto"
      >
        <div className="relative flex flex-col h-full">
          <div className="p-4 md:p-6">
            <DialogTitle className="text-xl font-semibold">
              Edit HOA
            </DialogTitle>
            <button
              onClick={handleDialogClose}
              className="absolute right-4 top-4 p-2 hover:bg-zinc-100 dark:hover:bg-zinc-700 rounded-full transition-colors duration-150"
              aria-label="Close dialog"
            >
              <XMarkIcon className="h-5 w-5 text-zinc-600 dark:text-zinc-100" />
            </button>

            <Divider className="my-4" />

            <div className="mb-4">
              <ShowAlert
                alertType="error"
                alertTitle={saveErrorTitle}
                alertMessage={saveError}
                isVisible={showSaveError}
                onClose={() => setShowSaveError(false)}
                timeout={5000}
                isInline={true}
              />
            </div>

            <DialogBody>
              <Fieldset>
                <FieldGroup>
                  {/* HOA Name and Active Status */}
                  <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                    <Field className="col-span-1 md:col-span-4">
                      <Label htmlFor="name">HOA Name*</Label>
                      <Input
                        id="name"
                        name="name"
                        value={editedHoa?.name}
                        onChange={handleInputChange}
                        onBlur={handleBlur}
                        placeholder="Enter HOA name"
                        required
                        className="w-full"
                        data-invalid={touchedFields.name && formErrors.name}
                      />
                      {touchedFields.name && formErrors.name && (
                        <ErrorMessage>{formErrors.name}</ErrorMessage>
                      )}
                    </Field>
                    <Field className="col-span-1">
                      <div className="flex items-center h-10 mt-2">
                        <input
                          id="is_active"
                          type="checkbox"
                          name="is_active"
                          checked={editedHoa?.is_active}
                          onChange={handleCheckboxChange}
                          className={clsx('mr-2', checkboxClass)}
                        />
                        <Label htmlFor="is_active">Is Active</Label>
                      </div>
                    </Field>
                  </div>

                  {/* Address Fields */}
                  <div className="space-y-4">
                    <Field>
                      <Label htmlFor="street">Street*</Label>
                      <Input
                        id="street"
                        name="street"
                        value={editedHoa?.street}
                        onChange={handleInputChange}
                        onBlur={handleBlur}
                        placeholder="Enter street address"
                        required
                        className="w-full"
                        data-invalid={touchedFields.street && formErrors.street}
                      />
                      {touchedFields.street && formErrors.street && (
                        <ErrorMessage>{formErrors.street}</ErrorMessage>
                      )}
                    </Field>

                    <Field>
                      <Label htmlFor="street2">Street 2</Label>
                      <Input
                        id="street2"
                        name="street2"
                        value={editedHoa?.street2 || ''}
                        onChange={handleInputChange}
                        onBlur={handleBlur}
                        placeholder="Enter additional street information (optional)"
                        className="w-full"
                      />
                    </Field>
                  </div>

                  {/* City, State, ZIP */}
                  <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                    <Field className="col-span-1 md:col-span-2">
                      <Label htmlFor="city">City*</Label>
                      <Input
                        id="city"
                        name="city"
                        value={editedHoa?.city}
                        onChange={handleInputChange}
                        onBlur={handleBlur}
                        placeholder="Enter city"
                        required
                        className="w-full"
                        data-invalid={touchedFields.city && formErrors.city}
                      />
                      {touchedFields.city && formErrors.city && (
                        <ErrorMessage>{formErrors.city}</ErrorMessage>
                      )}
                    </Field>

                    <Field className="col-span-1">
                      <Label htmlFor="state">State*</Label>
                      <FieldPicker
                        label="State"
                        options={usStates}
                        value={editedHoa?.state}
                        onChange={(value) =>
                          handleInputChange({
                            target: {
                              name: 'state',
                              value,
                              type: 'change',
                              checked: false,
                              nodeName: 'INPUT',
                            } as unknown as EventTarget & HTMLInputElement,
                          } as React.ChangeEvent<HTMLInputElement>)
                        }
                        placeholder="State"
                        invalid={!!(touchedFields.state && formErrors.state)}
                      />
                      {touchedFields.state && formErrors.state && (
                        <ErrorMessage>{formErrors.state}</ErrorMessage>
                      )}
                    </Field>

                    <Field className="col-span-1 md:col-span-2">
                      <Label htmlFor="zip">ZIP Code*</Label>
                      <Input
                        id="zip"
                        name="zip"
                        value={editedHoa?.zip}
                        onChange={handleInputChange}
                        onBlur={handleBlur}
                        placeholder="Enter ZIP code"
                        required
                        className="w-full"
                        data-invalid={touchedFields.zip && formErrors.zip}
                      />
                      {touchedFields.zip && formErrors.zip && (
                        <ErrorMessage>{formErrors.zip}</ErrorMessage>
                      )}
                    </Field>
                  </div>

                  {/* Contact Information */}
                  <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                    <Field className="col-span-1 md:col-span-3">
                      <Label htmlFor="contact_email">Contact Email*</Label>
                      <Input
                        id="contact_email"
                        name="contact_email"
                        type="email"
                        value={editedHoa?.contact_email || ''}
                        onChange={handleInputChange}
                        onBlur={handleBlur}
                        placeholder="Enter contact email"
                        className="w-full"
                        data-invalid={
                          touchedFields.contact_email &&
                          formErrors.contact_email
                        }
                      />
                      {touchedFields.contact_email &&
                        formErrors.contact_email && (
                          <ErrorMessage>
                            {formErrors.contact_email}
                          </ErrorMessage>
                        )}
                    </Field>

                    <Field className="col-span-1 md:col-span-2">
                      <Label htmlFor="contact_phone">Contact Phone*</Label>
                      <Input
                        id="contact_phone"
                        name="contact_phone"
                        type="tel"
                        value={
                          editedHoa?.contact_phone
                            ? formatPhoneNumber(editedHoa.contact_phone)
                            : ''
                        }
                        onChange={handleInputChange}
                        onBlur={handleBlur}
                        placeholder="Enter contact phone"
                        className="w-full"
                        data-invalid={
                          touchedFields.contact_phone &&
                          formErrors.contact_phone
                        }
                      />
                      {touchedFields.contact_phone &&
                        formErrors.contact_phone && (
                          <ErrorMessage>
                            {formErrors.contact_phone}
                          </ErrorMessage>
                        )}
                    </Field>
                  </div>

                  {/* Website */}
                  <Field>
                    <Label htmlFor="website_url">Website URL</Label>
                    <Input
                      id="website_url"
                      name="website_url"
                      type="url"
                      value={editedHoa?.website_url || ''}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      placeholder="Enter website URL"
                      className="w-full"
                      data-invalid={
                        touchedFields.website_url && formErrors.website_url
                      }
                    />
                    {touchedFields.website_url && formErrors.website_url && (
                      <ErrorMessage>{formErrors.website_url}</ErrorMessage>
                    )}
                  </Field>

                  {/* Description */}
                  <Field>
                    <Label htmlFor="description">Description</Label>
                    <Textarea
                      id="description"
                      name="description"
                      value={editedHoa?.description || ''}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      placeholder="500 character limit"
                      rows={3}
                      maxLength={500}
                      className="w-full"
                    />
                  </Field>
                </FieldGroup>
              </Fieldset>
            </DialogBody>
          </div>

          <div className="sticky bottom-0 mt-auto border-t border-gray-200 dark:border-gray-700 bg-stone-100 dark:bg-gray-800 p-4 md:p-6">
            <div className="flex flex-col space-y-3">
              <Button
                onClick={handleSave}
                color="sky"
                disabled={!isFormValid() || isSaving}
                className="w-full disabled:opacity-50"
              >
                {isSaving ? 'Saving...' : 'Save'}
              </Button>
              <Button
                onClick={handleCancel}
                color="zinc"
                disabled={isSaving}
                className="w-full"
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      </Dialog>

      {/* Send HOA Message Dialog */}
      {hoa && (
        <SendHoaMessageDialog
          isOpen={isMessageDialogOpen}
          onClose={() => setIsMessageDialogOpen(false)}
          onSend={handleSendMessage}
          hoa={hoa}
          senderName={senderName}
        />
      )}
    </SidebarLayout>
  );
};

export default HOADetailPage;
